import type { ModuleKey } from '@esa/shared';
import { useState, type ReactNode } from 'react';
import type { TenantRole } from '@esa/shared';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  CalendarClock,
  CircleCheck,
  CircleOff,
  Eye,
  History,
  Lock,
  Pencil,
  Settings2,
  Target,
  Trash2,
  Users,
  Workflow,
} from 'lucide-react';
import { canAccess, capabilitiesIn, hasRole, type AdditiveGrant } from '../lib/access.js';
import { ApiError, api, type TenantConfig } from '../lib/api.js';
import { ago, hours } from '../lib/format.js';
import {
  AccessDenied,
  Badge,
  Button,
  Callout,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  Spinner,
  Tabs,
  TabsContent,
  Tooltip,
  cn,
  useToast,
  Select,
} from '../ui/index.js';
import { DataTable } from '../components/charts.js';
import {
  CalendarForm,
  ConfirmAction,
  EventTypeForm,
  FieldDefForm,
  GrantForm,
  NewButton,
  QueueForm,
  RequestTypeForm,
  RuleForm,
  SettingsForm,
  SlaForm,
} from '../components/config/forms.js';
import { useConfigWrite } from '../components/config/useConfigWrite.js';
import { WebhooksTab } from '../components/config/WebhooksTab.js';
import { KnowledgeTab } from '../components/config/KnowledgeTab.js';
import { MailTab } from '../components/config/MailTab.js';
import { IntakeTab } from '../components/config/IntakeTab.js';
import { ConfigNav } from '../components/config/ConfigNav.js';
import { QueueMembers } from '../components/config/QueueMembers.js';
import { PlatformSettingsForm } from '../components/config/PlatformSettingsForm.js';
import { ActionsTab } from '../components/config/ActionsTab.js';
import { ModulesTab } from '../components/config/ModulesTab.js';
import { IntegrationsTab } from '../components/config/IntegrationsTab.js';
import { PlatformTab } from '../components/config/PlatformTab.js';
import { TelemetryTab } from '../components/telemetry/TelemetryTab.js';
import { WorkerPanel } from '../components/config/WorkerPanel.js';
import { DesksPanel } from '../components/config/DesksPanel.js';
import { confirmDiscard } from '../lib/unsaved.js';
import { IntakeKeysPanel } from '../components/config/IntakeKeysPanel.js';
import { ChangelogPanel } from '../components/config/ChangelogPanel.js';
import { RoleCapabilitiesPanel } from '../components/config/RoleCapabilitiesPanel.js';
import { BugReportsPanel } from '../components/config/BugReportsPanel.js';

interface Ctx {
  tenant: string;
  role: string | null;
  tenantName: string | undefined;
  /** /me, for this desk's capabilities: a Request manager sees only the Requests pages. */
  me?: { tenants: { id: string; capabilities?: string[]; grants?: string[] }[] };
}

/** The roles a grant can be changed between, lowest first. */
const ROLE_OPTIONS = [
  { value: 'requester', label: 'Requester' },
  { value: 'agent', label: 'Agent' },
  { value: 'queue_lead', label: 'Queue lead' },
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'platform_admin', label: 'Platform admin' },
];

/** The pages a Request manager (config.requests without config.write) may open. */
const REQUEST_TABS = new Set(['fields', 'rules', 'sla', 'queues', 'history']);

interface AdminField {
  key: string;
  version: number;
  label: string;
  field_type: string;
  required_at: string;
  visibility: string;
  consumer: string;
  active: boolean;
  request_type_keys: string[] | null;
}

interface AdminRule {
  id: string;
  name: string;
  sort_order: number;
  active: boolean;
  version: number;
  predicate: { all: { field: string; op: string; value?: unknown }[] };
  action: Record<string, unknown>;
}

interface HistoryRow {
  entity: string;
  entity_id: string;
  version: number;
  at: string;
  actor_email: string | null;
  actor_name: string | null;
}

/**
 * Configuration. Everything here is data a tenant admin changes without a
 * deployment, and every change is versioned and attributed.
 *
 * The screen is deliberately not a generic CRUD grid. Each entity is edited
 * through a form that states the rule it has to satisfy — a field needs a
 * consumer, a queue cannot be closed while it holds live work, a field edit
 * creates a version rather than a mutation — because those rules are the
 * argument for the whole system, and hiding them behind a bare "Save" throws
 * that away.
 */
export function AdminPage() {
  const { tenant, role, tenantName, me } = useOutletContext<Ctx>();
  const caps = capabilitiesIn(me, tenant);
  const allowed = canAccess(role, 'admin', caps);
  // Everything, or only how requests are handled (a Request manager).
  const full = caps ? caps.includes('config.write') : hasRole(role, 'tenant_admin');
  // All of Access, or granting up to one's own level (a Request manager).
  const manageRoles = caps ? caps.includes('roles.manage') : hasRole(role, 'tenant_admin');
  const delegate = manageRoles || Boolean(caps?.includes('roles.delegate'));
  const myGrants = me?.tenants.find((t) => t.id === tenant)?.grants ?? [];
  const mayHandRole = (r: string) => manageRoles || (hasRole(role, r as TenantRole) && r !== 'platform_admin');
  const mayHandGrant = (g: AdditiveGrant) => manageRoles || myGrants.includes(g);
  const write = useConfigWrite(tenant);
  // The open tab lives in the URL: "the SLA targets are wrong" is a link, not
  // a set of directions.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') ?? 'fields';

  // Asks first when the tab being left has unsaved changes (lib/unsaved.ts).
  const goToTab = (v: string) => {
    if (!confirmDiscard()) return;
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set('tab', v);
        return next;
      },
      { replace: true },
    );
  };

  const cfg = useQuery({
    queryKey: ['config', tenant],
    queryFn: () => api.get<TenantConfig>(`/api/v1/tenants/${tenant}/config`),
    enabled: Boolean(tenant) && allowed,
  });

  /**
   * Undefined while the config loads: do not filter yet, rather than flash a
   * half-empty tab strip. The server refuses independently.
   */
  const modules = cfg.data?.modules;
  const has = (m: ModuleKey) => !modules || modules.includes(m);

  // A deep link to a tab this group has not got — a bookmark from before it
  // was switched off — would otherwise render an empty panel with no
  // explanation.
  const tab = !full && !REQUEST_TABS.has(tabParam) && !(tabParam === 'roles' && delegate) ? 'fields'
    : tabParam === 'webhooks' && !has('webhooks') ? 'fields'
    : tabParam === 'knowledge' && !has('knowledge') ? 'fields'
    : tabParam;

  const fields = useQuery({
    queryKey: ['admin-fields', tenant],
    queryFn: () => api.get<{ fields: AdminField[] }>(`/api/v1/tenants/${tenant}/admin/fields`),
    enabled: Boolean(tenant) && allowed,
  });

  const rules = useQuery({
    queryKey: ['admin-rules', tenant],
    queryFn: () => api.get<{ rules: AdminRule[] }>(`/api/v1/tenants/${tenant}/admin/rules`),
    enabled: Boolean(tenant) && allowed,
  });

  const roles = useQuery({
    queryKey: ['admin-roles', tenant],
    queryFn: () =>
      api.get<{
        grants: {
          id: string;
          email: string;
          name: string | null;
          role: string;
          granted_at: string;
          revoked_at: string | null;
        }[];
        /** Held ALONGSIDE a rank role, not instead of one (FR-AD-06). */
        extraGrants: {
          id: string;
          email: string;
          name: string | null;
          grant_key: string;
          granted_at: string;
          revoked_at: string | null;
        }[];
      }>(`/api/v1/tenants/${tenant}/admin/roles`),
    enabled: Boolean(tenant) && allowed && delegate,
  });

  const history = useQuery({
    queryKey: ['admin-history', tenant],
    queryFn: () =>
      api.get<{ history: HistoryRow[] }>(`/api/v1/tenants/${tenant}/admin/history?limit=12`),
    enabled: Boolean(tenant) && allowed,
  });

  if (!allowed) {
    return <AccessDenied area="admin" role={role} tenantName={tenantName} />;
  }

  const activeFields = fields.data?.fields.filter((f) => f.active) ?? [];
  const activeRules = rules.data?.rules.filter((r) => r.active) ?? [];
  const nextRuleOrder =
    activeRules.length === 0 ? 10 : Math.max(...activeRules.map((r) => r.sort_order)) + 10;
  const nextTypeOrder =
    (cfg.data?.requestTypes.length ?? 0) === 0
      ? 10
      : Math.max(...(cfg.data?.requestTypes ?? []).map((t) => t.sortOrder)) + 10;

  /** Swap a rule with its neighbour. Two patches, each sending only sortOrder. */
  const reorder = (rule: AdminRule, dir: -1 | 1) => {
    const ordered = [...activeRules].sort((a, b) => a.sort_order - b.sort_order);
    const i = ordered.findIndex((r) => r.id === rule.id);
    const other = ordered[i + dir];
    if (!other) return;
    void write
      .mutateAsync({
        method: 'patch',
        path: `/api/v1/tenants/${tenant}/admin/rules/${rule.id}`,
        body: { sortOrder: other.sort_order },
        done: `"${rule.name}" moved ${dir === -1 ? 'up' : 'down'}`,
      })
      .then(() =>
        write.mutateAsync({
          method: 'patch',
          path: `/api/v1/tenants/${tenant}/admin/rules/${other.id}`,
          body: { sortOrder: rule.sort_order },
          done: 'Order saved',
        }),
      )
      .catch(() => undefined);
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 sm:p-6">
      <PageHeader
        title="Configuration"
        description={
          <>
            {cfg.data?.name} · every change here is versioned and attributed, and none of it needs a
            deployment
          </>
        }
        actions={
          <SettingsForm
            tenant={tenant}
            config={cfg.data}
            requestsOnly={!full}
            trigger={
              // Not openable until the settings it shows have loaded.
              <Button size="sm" variant="secondary" disabled={!cfg.data}>
                <Settings2 aria-hidden className="h-3.5 w-3.5" />
                Tenant settings
              </Button>
            }
          />
        }
      />

      <Tabs
        value={tab}
        onValueChange={(v) => goToTab(v)}
      >
        {/* Grouped down the side (ConfigNav): one row of tabs ran to
            seventeen and wrapped its own labels. The same gates as before —
            a page for a feature this group has not got is not offered, and
            the platform pages are for platform admins only. */}
        <div className="grid gap-6 lg:grid-cols-[12rem_minmax(0,1fr)]">
        <ConfigNav
          value={tab}
          onChange={(v) => goToTab(v)}
          sections={[
            {
              title: 'Requests',
              items: [
                { value: 'fields', label: 'Fields', count: activeFields.length },
                { value: 'rules', label: 'Routing', count: activeRules.length },
                { value: 'sla', label: 'SLA', count: cfg.data?.slaPolicies.length },
                { value: 'queues', label: 'Queues & types', count: cfg.data?.queues.length },
              ],
            },
            {
              title: 'Email & intake',
              items: [
                { value: 'mail', label: 'Mail', show: full },
                { value: 'intake', label: 'Intake', show: full },
              ],
            },
            {
              title: 'Knowledge',
              items: [{ value: 'knowledge', label: 'Knowledge', show: full && has('knowledge') }],
            },
            {
              title: 'People & access',
              items: [
                { value: 'roles', label: 'Access', show: delegate },
                { value: 'keys', label: 'Integrations', show: full },
              ],
            },
            {
              title: 'Automation',
              items: [
                { value: 'webhooks', label: 'Webhooks', show: full && has('webhooks') },
                { value: 'actions', label: 'Actions', show: role === 'platform_admin' && has('actions') },
              ],
            },
            {
              title: 'Platform',
              items: [
                { value: 'modules', label: 'Features', show: role === 'platform_admin' },
                { value: 'platform', label: 'Platform', show: role === 'platform_admin' },
                { value: 'desks', label: 'Desks', show: role === 'platform_admin' },
                { value: 'environment', label: 'Environment', show: role === 'platform_admin' },
                { value: 'worker', label: 'Worker', show: role === 'platform_admin' },
                { value: 'telemetry', label: 'Telemetry', show: role === 'platform_admin' },
                { value: 'bugs', label: 'Bug reports', show: role === 'platform_admin' },
                { value: 'changelog', label: 'Changelog', show: role === 'platform_admin' },
              ],
            },
            { title: null, items: [{ value: 'history', label: 'History' }] },
          ]}
        />
        <div className="min-w-0">

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="fields" className="focus-visible:outline-none">
          <Panel
            title="Field definitions"
            subtitle="A field cannot be created without a stated consumer — a report, a routing rule, or an automation"
            action={
              <FieldDefForm
                tenant={tenant}
                config={cfg.data}
                trigger={<NewButton>New field</NewButton>}
              />
            }
            footer={
              <p className="text-2xs leading-relaxed text-ink-tertiary">
                Definitions are versioned: editing a field creates a new version and historic
                tickets keep the one they were created under. Renaming an option cannot silently
                rewrite last year's reporting.
              </p>
            }
          >
            {fields.isLoading && <Spinner />}
            {fields.error && <ErrorState error={fields.error} />}
            {fields.data && activeFields.length === 0 && (
              <p className="py-6 text-center text-xs text-ink-tertiary">
                No fields yet. The ticket core works without any — add one when a report or a rule
                needs it.
              </p>
            )}
            {fields.data && (
              <ul className="space-y-2">
                {activeFields.map((f) => (
                  <li
                    key={`${f.key}-${f.version}`}
                    className="group rounded-md border border-line-subtle bg-surface-sunken/40 p-3 transition-colors hover:border-line"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-ink">{f.label}</span>
                      <code className="rounded-xs bg-surface px-1 font-mono text-2xs text-ink-tertiary ring-1 ring-inset ring-line">
                        {f.key}
                      </code>
                      <Badge tone="neutral">{f.field_type}</Badge>
                      <Badge tone="neutral">v{f.version}</Badge>
                      {f.required_at !== 'never' && (
                        <Badge tone="warn" icon={<Target />}>
                          required at {f.required_at}
                        </Badge>
                      )}
                      <Badge
                        tone={f.visibility === 'requester' ? 'info' : 'neutral'}
                        icon={f.visibility === 'requester' ? <Eye /> : <Lock />}
                      >
                        {f.visibility === 'requester' ? 'requester can set' : 'agent only'}
                      </Badge>

                      <span className="ml-auto flex items-center gap-1 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <FieldDefForm
                          tenant={tenant}
                          config={cfg.data}
                          existing={f}
                          trigger={
                            <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${f.label}`}>
                              <Pencil aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                        />
                        <ConfirmAction
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label={`Retire ${f.label}`}
                            >
                              <Trash2 aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                          title={`Retire "${f.label}"?`}
                          body={
                            <>
                              New tickets will stop offering this field. The definition itself is
                              kept, so every ticket that already carries a value can still be read
                              — retiring is not deleting.
                            </>
                          }
                          confirmLabel="Retire field"
                          pending={write.isPending}
                          onConfirm={() =>
                            write.mutateAsync({
                              method: 'del',
                              path: `/api/v1/tenants/${tenant}/admin/fields/${f.key}`,
                              done: `"${f.label}" retired`,
                            })
                          }
                        />
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-ink-secondary">
                      <span className="font-medium text-ink">Consumer:</span> {f.consumer}
                    </p>
                    {f.request_type_keys && f.request_type_keys.length > 0 && (
                      <p className="mt-1 text-2xs text-ink-tertiary">
                        Only for: {f.request_type_keys.join(', ')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="rules" className="focus-visible:outline-none">
          <Panel
            title="Routing rules"
            subtitle="Ordered, first match wins, and able to predicate on the requester — not only the ticket text"
            action={
              <RuleForm
                tenant={tenant}
                config={cfg.data}
                nextSortOrder={nextRuleOrder}
                trigger={<NewButton>New rule</NewButton>}
              />
            }
            footer={
              <p className="text-2xs leading-relaxed text-ink-tertiary">
                A candidate rule set can be replayed against historic tickets before it goes live,
                so a routing change is judged on outcomes rather than intentions. Deactivating keeps
                the rule: it is the explanation for how past tickets were routed.
              </p>
            }
          >
            {rules.isLoading && <Spinner />}
            {rules.error && <ErrorState error={rules.error} />}
            {rules.data && activeRules.length === 0 && (
              <p className="py-6 text-center text-xs text-ink-tertiary">
                No active rules. Everything lands in the default queue and the classification ladder
                does the work.
              </p>
            )}
            {rules.data && (
              <ol className="space-y-2">
                {[...activeRules]
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((r, i, all) => (
                    <li
                      key={r.id}
                      className="group rounded-md border border-line-subtle bg-surface-sunken/40 p-3 transition-colors hover:border-line"
                    >
                      <div className="flex items-center gap-2">
                        <span className="nums flex h-5 w-5 shrink-0 items-center justify-center rounded-xs bg-surface text-2xs font-semibold text-ink-secondary ring-1 ring-inset ring-line">
                          {r.sort_order}
                        </span>
                        <span className="text-sm font-medium text-ink">{r.name}</span>
                        {r.active ? (
                          <Badge tone="ok" icon={<CircleCheck />}>
                            active
                          </Badge>
                        ) : (
                          <Badge tone="neutral" icon={<CircleOff />}>
                            inactive
                          </Badge>
                        )}

                        <span className="ml-auto flex items-center gap-0.5 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                          <Tooltip content="Move earlier">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              disabled={i === 0 || write.isPending}
                              onClick={() => reorder(r, -1)}
                              aria-label={`Move ${r.name} earlier`}
                            >
                              <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Move later">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              disabled={i === all.length - 1 || write.isPending}
                              onClick={() => reorder(r, 1)}
                              aria-label={`Move ${r.name} later`}
                            >
                              <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          </Tooltip>
                          <RuleForm
                            tenant={tenant}
                            config={cfg.data}
                            existing={r}
                            nextSortOrder={r.sort_order}
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Edit ${r.name}`}
                              >
                                <Pencil aria-hidden className="h-3.5 w-3.5" />
                              </Button>
                            }
                          />
                          <ConfirmAction
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Deactivate ${r.name}`}
                              >
                                <Trash2 aria-hidden className="h-3.5 w-3.5" />
                              </Button>
                            }
                            title={`Deactivate "${r.name}"?`}
                            body={
                              <>
                                New mail stops being matched against it. The rule itself is kept,
                                because it is the explanation for where the tickets it already
                                routed ended up.
                              </>
                            }
                            confirmLabel="Deactivate rule"
                            pending={write.isPending}
                            onConfirm={() =>
                              write.mutateAsync({
                                method: 'del',
                                path: `/api/v1/tenants/${tenant}/admin/rules/${r.id}`,
                                done: `"${r.name}" deactivated`,
                              })
                            }
                          />
                        </span>
                      </div>

                      <div className="mt-2 space-y-0.5 font-mono text-2xs leading-relaxed">
                        {r.predicate.all.map((c, j) => (
                          <p key={j}>
                            <span className="text-ink-tertiary">{j === 0 ? 'when' : ' and'}</span>{' '}
                            <span className="font-semibold text-ink">{c.field}</span>{' '}
                            <span className="text-brand">{c.op}</span>{' '}
                            <span className="text-ink-secondary">
                              {c.value === undefined
                                ? ''
                                : Array.isArray(c.value)
                                  ? `[${c.value.join(', ')}]`
                                  : String(c.value)}
                            </span>
                          </p>
                        ))}
                        <p className="flex items-center gap-1 pt-0.5">
                          <Workflow aria-hidden className="h-3 w-3 text-ink-tertiary" />
                          <span className="text-ink-tertiary">then</span>{' '}
                          <span className="text-ink">{JSON.stringify(r.action)}</span>
                        </p>
                      </div>
                    </li>
                  ))}
              </ol>
            )}
          </Panel>
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="sla" className="focus-visible:outline-none">
          <div className="space-y-4">
            <Panel
              title="SLA policies"
              subtitle="Per tenant and per request type, with a business-hours calendar"
              action={
                <SlaForm
                  tenant={tenant}
                  config={cfg.data}
                  trigger={<NewButton>New policy</NewButton>}
                />
              }
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  Targets are consumed in business hours, and the clock pauses whenever the ticket is
                  waiting on somebody else. A team breaching half its work under one target is a
                  signal about the target as much as the team.
                </p>
              }
            >
              {!cfg.data ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <ul className="divide-y divide-line-subtle">
                  {cfg.data.slaPolicies.map((p) => (
                    <li key={p.key} className="group flex items-center gap-3 py-2 first:pt-0">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink">{p.name}</p>
                        <p className="text-2xs text-ink-tertiary">
                          {p.requestTypeKey ?? 'everything else (default)'} · {p.calendarKey}
                        </p>
                      </div>
                      <span className="nums shrink-0 text-xs text-ink-secondary">
                        {hours(p.firstResponseMins / 60)} to respond
                      </span>
                      <span className="nums shrink-0 text-xs text-ink-secondary">
                        {hours(p.resolutionMins / 60)} to resolve
                      </span>
                      <span className="nums w-12 shrink-0 text-right text-2xs text-ink-tertiary">
                        at {p.atRiskPct}%
                      </span>
                      <span className="shrink-0 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <SlaForm
                          tenant={tenant}
                          config={cfg.data}
                          existing={p}
                          trigger={
                            <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${p.name}`}>
                              <Pencil aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title="Business hours"
              subtitle="What every target above is measured against"
            >
              {!cfg.data ? (
                <Skeleton className="h-16 w-full" />
              ) : cfg.data.calendars.length === 0 ? (
                <p className="text-xs text-ink-tertiary">No calendar configured.</p>
              ) : (
                <ul className="space-y-2">
                  {cfg.data.calendars.map((c) => (
                    <li
                      key={c.key}
                      className="group flex items-center gap-3 rounded-md border border-line-subtle bg-surface-sunken/40 p-3"
                    >
                      <CalendarClock aria-hidden className="h-4 w-4 shrink-0 text-ink-tertiary" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink">
                          {c.key}{' '}
                          <span className="text-2xs font-normal text-ink-tertiary">
                            {c.timezone}
                          </span>
                        </p>
                        <p className="text-2xs text-ink-tertiary">
                          {c.hours.length === 0
                            ? 'never open'
                            : c.hours
                                .map(
                                  (h) =>
                                    `${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][h.dow - 1]} ${h.start}–${h.end}`,
                                )
                                .join(' · ')}
                        </p>
                        <p className="mt-0.5 text-2xs text-ink-tertiary">
                          {c.holidays.length} holiday{c.holidays.length === 1 ? '' : 's'} observed
                        </p>
                      </div>
                      <span className="shrink-0 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <CalendarForm
                          tenant={tenant}
                          existing={c}
                          trigger={
                            <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${c.key}`}>
                              <Pencil aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="queues" className="focus-visible:outline-none">
          <div className="grid gap-4 md:grid-cols-2">
            <Panel
              title="Queues"
              action={
                <QueueForm tenant={tenant} agents={cfg.data?.agents} trigger={<NewButton>New queue</NewButton>} />
              }
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  A queue cannot be closed while it still holds open tickets, and the default queue
                  cannot be closed at all — routing has to have somewhere to land.
                </p>
              }
            >
              <ul className="space-y-1">
                {cfg.data?.queues.map((qq) => (
                  <li
                    key={qq.key}
                    className="group flex items-center gap-2 rounded-xs px-1 py-1 text-sm hover:bg-surface-hover"
                  >
                    <span className="text-ink">{qq.name}</span>
                    <code className="rounded-xs bg-surface-sunken px-1 font-mono text-2xs text-ink-tertiary">
                      {qq.key}
                    </code>
                    {qq.isDefault && <Badge tone="brand">default</Badge>}
                    {qq.restricted && (
                      <Badge tone="warn" icon={<Lock />}>
                        restricted
                      </Badge>
                    )}
                    <span className="ml-auto flex items-center gap-0.5 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                      {qq.restricted && (
                        <QueueMembers
                          tenant={tenant}
                          queue={qq}
                          trigger={
                            <Button variant="ghost" size="sm" iconOnly aria-label={`Who can see ${qq.name}`}>
                              <Users aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                        />
                      )}
                      <QueueForm
                        tenant={tenant}
                        existing={qq}
                        agents={cfg.data?.agents}
                        trigger={
                          <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${qq.name}`}>
                            <Pencil aria-hidden className="h-3.5 w-3.5" />
                          </Button>
                        }
                      />
                      {!qq.isDefault && (
                        <ConfirmAction
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label={`Close ${qq.name}`}
                            >
                              <Trash2 aria-hidden className="h-3.5 w-3.5" />
                            </Button>
                          }
                          title={`Close "${qq.name}"?`}
                          body={
                            <>
                              The queue stops being offered. If it still holds open tickets the
                              server will refuse and tell you how many — orphaning live work is not
                              a configuration change.
                            </>
                          }
                          confirmLabel="Close queue"
                          pending={write.isPending}
                          onConfirm={() =>
                            write.mutateAsync({
                              method: 'del',
                              path: `/api/v1/tenants/${tenant}/admin/queues/${qq.key}`,
                              done: `"${qq.name}" closed`,
                            })
                          }
                        />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel
              title="Request types"
              subtitle="In ladder priority order — most specific first"
              action={
                <RequestTypeForm
                  tenant={tenant}
                  nextSortOrder={nextTypeOrder}
                  trigger={<NewButton>New type</NewButton>}
                />
              }
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  Purity below this tenant's threshold (
                  {Math.round((cfg.data?.settings.autoRouteThreshold ?? 0.9) * 100)}%) means the
                  router refuses to guess and asks a person instead.
                </p>
              }
            >
              <ol className="space-y-0.5">
                {cfg.data?.requestTypes.map((rt) => (
                  <li
                    key={rt.key}
                    className="group flex items-center gap-2 rounded-xs px-1 py-0.5 text-xs hover:bg-surface-hover"
                  >
                    <span className="truncate text-ink-secondary">{rt.label}</span>
                    {rt.acknowledgement && (
                      <span className="shrink-0 rounded-xs bg-brand-tint px-1 text-2xs text-brand" title="Has its own acknowledgement">
                        own reply
                      </span>
                    )}
                    {rt.measuredPurity !== null && (
                      <span
                        className={cn(
                          'nums ml-auto shrink-0 text-2xs',
                          rt.measuredPurity < (cfg.data?.settings.autoRouteThreshold ?? 0.9)
                            ? 'font-semibold text-warn'
                            : 'text-ink-tertiary',
                        )}
                        title="Measured routing purity over six months"
                      >
                        {Math.round(rt.measuredPurity * 100)}%
                      </span>
                    )}
                    <span
                      className={cn(
                        'flex shrink-0 items-center gap-0.5 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
                        rt.measuredPurity === null && 'ml-auto',
                      )}
                    >
                      <RequestTypeForm
                        tenant={tenant}
                        existing={rt}
                        nextSortOrder={rt.sortOrder}
                        trigger={
                          <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${rt.label}`}>
                            <Pencil aria-hidden className="h-3 w-3" />
                          </Button>
                        }
                      />
                      <ConfirmAction
                        trigger={
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={`Retire ${rt.label}`}
                          >
                            <Trash2 aria-hidden className="h-3 w-3" />
                          </Button>
                        }
                        title={`Retire "${rt.label}"?`}
                        body={
                          <>
                            New tickets can no longer be classified as this type. Tickets that
                            already carry it keep it, and the reports still show them.
                          </>
                        }
                        confirmLabel="Retire type"
                        pending={write.isPending}
                        onConfirm={() =>
                          write.mutateAsync({
                            method: 'del',
                            path: `/api/v1/tenants/${tenant}/admin/request-types/${rt.key}`,
                            done: `"${rt.label}" retired`,
                          })
                        }
                      />
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>

            <Panel
              title="Events"
              subtitle='What this desk can "Move to Event" onto'
              action={
                <EventTypeForm tenant={tenant} trigger={<NewButton>New event</NewButton>} />
              }
              footer={
                <p className="text-2xs leading-relaxed text-ink-tertiary">
                  An event cannot be retired while tickets are still parked on it — the server will
                  refuse and say how many, because retiring the catalogue entry a parked ticket
                  points to is how that ticket stops coming back.
                </p>
              }
            >
              <ul className="space-y-1">
                {cfg.data?.eventTypes.map((et) => (
                  <li
                    key={et.key}
                    className="group flex items-center gap-2 rounded-xs px-1 py-1 text-sm hover:bg-surface-hover"
                  >
                    <span className="text-ink">{et.name}</span>
                    <code className="rounded-xs bg-surface-sunken px-1 font-mono text-2xs text-ink-tertiary">
                      {et.key}
                    </code>
                    {et.defaultDays !== null && (
                      <span className="text-2xs text-ink-tertiary">~{et.defaultDays}d</span>
                    )}
                    <span className="ml-auto flex items-center gap-0.5 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                      <EventTypeForm
                        tenant={tenant}
                        existing={et}
                        trigger={
                          <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${et.name}`}>
                            <Pencil aria-hidden className="h-3.5 w-3.5" />
                          </Button>
                        }
                      />
                      <ConfirmAction
                        trigger={
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={`Retire ${et.name}`}
                          >
                            <Trash2 aria-hidden className="h-3.5 w-3.5" />
                          </Button>
                        }
                        title={`Retire "${et.name}"?`}
                        body={
                          <>
                            New tickets can no longer be parked on this event. If any ticket is
                            still waiting on it the server will refuse and say how many.
                          </>
                        }
                        confirmLabel="Retire event"
                        pending={write.isPending}
                        onConfirm={() =>
                          write.mutateAsync({
                            method: 'del',
                            path: `/api/v1/tenants/${tenant}/admin/event-types/${et.key}`,
                            done: `"${et.name}" retired`,
                          })
                        }
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="roles" className="focus-visible:outline-none">
          <Panel
            title="Access"
            subtitle="Grants are stored data with an explicit revocation, not a config file"
            action={
              <GrantForm
                tenant={tenant}
                mayGrantPlatformAdmin={role === 'platform_admin'}
                maxRole={manageRoles ? undefined : (role as TenantRole)}
                extras={(['request_manager', 'kb_reviewer'] as const).filter(mayHandGrant).map((g) => ({
                  key: g,
                  label: g === 'request_manager' ? 'Request manager' : 'Knowledge reviewer',
                  hint:
                    g === 'request_manager'
                      ? 'Configures fields, routing, SLAs, queues and request types, and grants access up to their own level.'
                      : 'Approves and publishes knowledge articles.',
                }))}
                trigger={<NewButton>Grant access</NewButton>}
              />
            }
          >
            {roles.isLoading && <Spinner />}
            {roles.error && <ErrorState error={roles.error} />}
            {roles.data && (
              <ul className="divide-y divide-line-subtle">
                {roles.data.grants
                  .filter((g) => g.role !== 'requester')
                  .map((g) => (
                    <li key={g.id} className="group flex items-center gap-3 py-2 first:pt-0">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-ink">{g.name ?? g.email}</p>
                        {g.name && <p className="truncate text-2xs text-ink-tertiary">{g.email}</p>}
                      </div>
                      {/* Changed in place: one step, and the history still
                          shows both grants. Only between roles this person
                          may hand out. */}
                      {!g.revoked_at && mayHandRole(g.role) ? (
                        <Select
                          size="sm"
                          className="w-36"
                          value={g.role}
                          onValueChange={(to: string) =>
                            void write.mutateAsync({
                              method: 'patch',
                              path: `/api/v1/tenants/${tenant}/admin/roles/${g.id}`,
                              body: { role: to },
                              done: `${g.name ?? g.email} is now ${to.replace('_', ' ')}`,
                            }).catch(() => undefined)
                          }
                          options={ROLE_OPTIONS.filter((o) => mayHandRole(o.value) && (o.value !== 'platform_admin' || role === 'platform_admin')).map((o) => ({ value: o.value, label: o.label }))}
                          aria-label={`Role for ${g.email}`}
                        />
                      ) : (
                        <Badge tone={g.revoked_at ? 'neutral' : 'brand'}>
                          {g.role.replace('_', ' ')}
                        </Badge>
                      )}
                      <span className="nums w-24 shrink-0 text-right text-2xs text-ink-tertiary">
                        {g.revoked_at ? 'revoked' : `since ${new Date(g.granted_at).toLocaleDateString('en-US')}`}
                      </span>
                      <span className="w-8 shrink-0">
                        {!g.revoked_at && mayHandRole(g.role) && (
                          <span className="opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <ConfirmAction
                              trigger={
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  aria-label={`Revoke ${g.email}`}
                                >
                                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                                </Button>
                              }
                              title="Revoke this grant?"
                              body={
                                <>
                                  {g.name ?? g.email} loses {g.role.replace('_', ' ')} access to this
                                  tenant immediately. The grant row is kept with a revocation
                                  timestamp, so the history of who could do what still reads
                                  correctly.
                                </>
                              }
                              confirmLabel="Revoke"
                              pending={write.isPending}
                              onConfirm={() =>
                                write.mutateAsync({
                                  method: 'del',
                                  path: `/api/v1/tenants/${tenant}/admin/roles/${g.id}`,
                                  done: `${g.email} revoked`,
                                })
                              }
                            />
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
            <Callout tone="neutral" className="mt-4">
              Break-glass access is a separate, audited path — never a stale environment variable
              consulted on every request.
            </Callout>

            {(['kb_reviewer', 'request_manager'] as const).filter(mayHandGrant).map((g) => (
              <div key={g} className="mt-5 border-t border-line-subtle pt-4">
                <ExtraGrants tenant={tenant} grantKey={g} rows={roles.data?.extraGrants ?? []} />
              </div>
            ))}
          </Panel>
          {manageRoles && (
            <div className="mt-4">
              <RoleCapabilitiesPanel tenant={tenant} />
            </div>
          )}
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="keys" className="focus-visible:outline-none">
          <IntegrationsTab tenant={tenant} />
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="knowledge" className="focus-visible:outline-none">
          <KnowledgeTab tenant={tenant} />
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="mail" className="focus-visible:outline-none">
          <MailTab tenant={tenant} config={cfg.data} />
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="intake" className="focus-visible:outline-none">
          <IntakeTab key={tenant} tenant={tenant} />
        </TabsContent>

        {role === 'platform_admin' && has('actions') && (
          <TabsContent value="actions" className="focus-visible:outline-none">
            <ActionsTab tenant={tenant} />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="modules" className="focus-visible:outline-none">
            <ModulesTab tenant={tenant} />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="environment" className="focus-visible:outline-none">
            <PlatformSettingsForm groups={['environment']} />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="platform" className="focus-visible:outline-none">
            <PlatformTab />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="bugs" className="focus-visible:outline-none">
            <BugReportsPanel />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="changelog" className="focus-visible:outline-none">
            <ChangelogPanel />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="desks" className="focus-visible:outline-none">
            <div className="space-y-4">
              <DesksPanel />
              <IntakeKeysPanel />
            </div>
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="worker" className="focus-visible:outline-none">
            <WorkerPanel />
          </TabsContent>
        )}

        {role === 'platform_admin' && (
          <TabsContent value="telemetry" className="focus-visible:outline-none">
            <TelemetryTab tenant={tenant} />
          </TabsContent>
        )}

        <TabsContent value="webhooks" className="focus-visible:outline-none">
          <WebhooksTab tenant={tenant} />
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="history" className="focus-visible:outline-none">
          <Panel
            title="Change history"
            subtitle="Every configuration write on this tenant, newest first"
            footer={
              <p className="text-2xs leading-relaxed text-ink-tertiary">
                This is not a log file that can be rotated away. Each version is a row, attributed
                to a person, next to the content that was written — so "who turned that off?" has an
                answer.
              </p>
            }
          >
            {history.isLoading && <Spinner />}
            {history.error && <ErrorState error={history.error} />}
            {history.data && history.data.history.length === 0 && (
              <p className="py-6 text-center text-xs text-ink-tertiary">
                Nothing has been changed yet. The current configuration is exactly what was seeded.
              </p>
            )}
            {history.data && history.data.history.length > 0 && (
              <DataTable
                head={['What', 'Which', 'Version', 'Who', 'When']}
                rows={history.data.history.map((h) => [
                  <span key="e" className="inline-flex items-center gap-1.5">
                    <History aria-hidden className="h-3 w-3 text-ink-tertiary" />
                    {h.entity.replace(/_/g, ' ')}
                  </span>,
                  <code key="i" className="font-mono text-2xs text-ink-secondary">
                    {h.entity_id.length > 24 ? `${h.entity_id.slice(0, 8)}…` : h.entity_id}
                  </code>,
                  `v${h.version}`,
                  h.actor_name ?? h.actor_email ?? 'unknown',
                  ago(h.at),
                ])}
              />
            )}
          </Panel>
        </TabsContent>
        </div>
        </div>
      </Tabs>
    </div>
  );
}

/**
 * Grants held beside the rank ladder (FR-AD-06..09): Knowledge reviewers and
 * Request managers.
 *
 * Deliberately their own lists, below the roles rather than mixed into them.
 * Neither is a rung on requester -> agent -> queue_lead -> admin; each sits
 * beside the ladder and is held AT THE SAME TIME as a rank role. Showing one
 * as another role is exactly the confusion the authorization change was made
 * to avoid -- and would imply that granting it replaces whatever the person
 * already had.
 */
const EXTRA_GRANT_COPY: Record<
  AdditiveGrant,
  { title: string; badge: string; about: ReactNode; none: string; add: string; granted: string; removed: string; placeholder: string }
> = {
  kb_reviewer: {
    title: 'Knowledge reviewers',
    badge: 'kb reviewer',
    about: (
      <>
        Held <strong className="text-ink-secondary">alongside</strong> the role above, not instead
        of it: an agent with this can approve and publish articles and nothing else. Administrators
        already have it. It stops working on its own if the person&rsquo;s agent role is revoked.
      </>
    ),
    none: 'Nobody here can review knowledge articles yet.',
    add: 'Add a reviewer',
    granted: 'Can now review knowledge articles',
    removed: 'Reviewer access removed',
    placeholder: 'areviewer@esassoc.com',
  },
  request_manager: {
    title: 'Request managers',
    badge: 'request manager',
    about: (
      <>
        Held <strong className="text-ink-secondary">alongside</strong> the role above: an agent with
        this can change how requests are handled &mdash; fields, routing, SLAs, queues and request
        types &mdash; and nothing else in Configuration. Not mail, intake, knowledge, webhooks or
        access. Administrators already have it. It stops working on its own if the person&rsquo;s
        agent role is revoked.
      </>
    ),
    none: 'Nobody here manages request configuration besides administrators.',
    add: 'Add a request manager',
    granted: 'Can now configure requests',
    removed: 'Request manager access removed',
    placeholder: 'amanager@esassoc.com',
  },
};

function ExtraGrants({
  tenant,
  grantKey,
  rows,
}: {
  tenant: string;
  grantKey: AdditiveGrant;
  rows: {
    id: string;
    email: string;
    name: string | null;
    grant_key: string;
    granted_at: string;
    revoked_at: string | null;
  }[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const copy = EXTRA_GRANT_COPY[grantKey];

  const after = () => void qc.invalidateQueries({ queryKey: ['admin-roles', tenant] });

  const grant = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/admin/extra-grants`, {
        email: email.trim(),
        grant: grantKey,
      }),
    onSuccess: () => {
      setEmail('');
      after();
      toast.success(copy.granted);
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'That could not be granted'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/tenants/${tenant}/admin/extra-grants/${id}`),
    onSuccess: () => {
      after();
      toast.success(copy.removed);
    },
  });

  const active = rows.filter((r) => !r.revoked_at && r.grant_key === grantKey);

  return (
    <div>
      <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">
        {copy.title}
      </p>
      <p className="mb-2.5 text-2xs leading-relaxed text-ink-tertiary">{copy.about}</p>

      {active.length === 0 ? (
        <p className="mb-2.5 text-2xs text-ink-tertiary">{copy.none}</p>
      ) : (
        <ul className="mb-2.5 space-y-1">
          {active.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs">
              <Badge tone="info">{copy.badge}</Badge>
              <span className="text-ink">{r.name ?? r.email}</span>
              <span className="text-2xs text-ink-tertiary">{r.email}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(r.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <Field label={copy.add} hint="They must already be an agent in this group.">
          <Input
            size="sm"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder={copy.placeholder}
            aria-label={`${copy.add}: email`}
          />
        </Field>
        <Button
          size="sm"
          disabled={email.trim() === '' || grant.isPending}
          loading={grant.isPending}
          onClick={() => grant.mutate()}
        >
          Grant
        </Button>
      </div>
    </div>
  );
}
